import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, kebabCase, compact } from 'lodash-es';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import withSmartListHandlers from 'site-modules/shared/components/color-feature-list-smart/color-feature-list-smart';

import './color-list-smart.scss';

function ColorList({
  listItems,
  inputType = 'checkbox',
  selectedItems = {},
  keyPrefix = '',
  itemClassName = '',
  className = '',
  onClick,
  showCheckmark = true,
  showPrice = false,
  ariaDescribedBy = '',
  ariaInvalid = false,
  isCustomAnyColor = false,
}) {
  return (
    <div
      className={`color-list-smart d-flex flex-wrap ${className}`}
      {...(ariaInvalid && ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy, 'aria-invalid': true } : {})}
    >
      {listItems.map(({ name, properties, price, disabled, trackingId, trackingValue }) => {
        const rgb = get(properties, 'rgb');
        const opacity = get(properties, 'opacity');
        const baseMSRP = get(price, 'baseMSRP', 0);

        const isTransparent = rgb === 'transparent';
        const isAnyColor = isCustomAnyColor && rgb === 'any';
        const priceString = showPrice && getPriceString(baseMSRP, '');
        const isSelected = !!selectedItems[name];

        return (
          rgb && (
            <label key={`${keyPrefix} ${name} (${rgb})`} htmlFor={`color-item-${name}`} className={itemClassName}>
              <input
                id={`color-item-${name}`}
                className={classnames(
                  'color-item d-flex rounded-circle p-0',
                  { any: isAnyColor },
                  { 'color-item-selected': isSelected },
                  { 'color-item-btn': onClick },
                  { 'unknown-color': isTransparent },
                  { disabled }
                )}
                style={{ backgroundColor: opacity ? `rgba(${rgb},${opacity})` : `rgb(${rgb})` }}
                name={`${kebabCase(keyPrefix)}color`}
                title={name}
                type={inputType}
                defaultChecked={isSelected}
                aria-label={compact([name, priceString]).join(' ')}
                onClick={onClick}
                data-tracking-id={trackingId}
                data-tracking-value={trackingValue}
                disabled={disabled}
              />
              {isAnyColor && (
                <svg className="any-icon" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
                  <rect x={0} y={0} width={10} height={10} fill="#005dc0" />
                  <rect x={12} y={0} width={10} height={10} fill="#e3a007" />
                  <rect x={0} y={12} width={10} height={10} fill="#ac1da7" />
                  <rect x={12} y={12} width={10} height={10} fill="#329300" />
                </svg>
              )}
              {showCheckmark && isSelected && <span className="icon-checkmark text-white" aria-hidden />}
              {priceString && (
                <span className={classnames('price small w-100', { 'fw-bold': isSelected })} aria-hidden="true">
                  {priceString}
                </span>
              )}
            </label>
          )
        );
      })}
    </div>
  );
}

ColorList.propTypes = {
  itemClassName: PropTypes.string,
  listItems: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      properties: PropTypes.shape({
        rgb: PropTypes.string,
        opacity: PropTypes.string,
      }),
      price: PropTypes.shape({
        baseMSRP: PropTypes.number,
      }),
      disabled: PropTypes.bool,
      trackingId: PropTypes.string,
      trackingValue: PropTypes.string,
    })
  ).isRequired,
  inputType: PropTypes.oneOf(['radio', 'checkbox']),
  keyPrefix: PropTypes.string,
  selectedItems: PropTypes.shape({}),
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  showCheckmark: PropTypes.bool,
  showPrice: PropTypes.bool,
  ariaDescribedBy: PropTypes.string,
  ariaInvalid: PropTypes.bool,
  isCustomAnyColor: PropTypes.bool,
};

export const ColorListSmart = withSmartListHandlers(ColorList, 'color');
